import React, { useEffect, useRef, useState } from "react";
import { useContext } from "react";
import { ChatContext } from "../../../contexts/ChatContext";
import ContactCard from "../ContactCard/ContactCard";
import GroupCard from "../ContactCard/GroupCard";
import "./Contacts.css";
import {
  Box,
  InputBase,
  Avatar,
  Paper,
  IconButton,
  Typography,
  Divider,
  Chip,
  Fade,
  Collapse,
  Badge,
  Tooltip,
  alpha,
  tooltipClasses,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Archive as ArchiveIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import CreateGroup from "../Group/CreateGroup";
import ChatServices from "../../../services/ChatServices";
import { connect } from "react-redux";
import { setCurrentUser } from "../../../redux/user/user.actions";

// Styled Components

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 8,
    padding: "6px 12px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const GradientHeader = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", // WhatsApp green
  color: "white",
  borderRadius: "0 0 24px 24px",
  padding: theme.spacing(2.5),
  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
  //borderBottom: '1px solid #e0e0e0',
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
  padding: "4px 12px",
  display: "flex",
  alignItems: "center",
  borderRadius: 50,
  backgroundColor: "#ffffff",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  // marginTop: theme.spacing(1),
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  border: "2px solid transparent",
  "&:focus-within": {
    backgroundColor: "white",
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  fontSize: "1rem",
  fontWeight: 600,
  backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
}));

const NewChatButton = styled(IconButton)(({ theme }) => ({
  background: "#ffffff",
  color: "#626060",
  width: 40,
  height: 40,
  "&:hover": {
    backgroundColor: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
  },
}));

const ArchiveSection = styled(Box)(({ theme }) => ({
  borderRadius: "8px",
  padding: theme.spacing(1.5),
  margin: theme.spacing(1, 0),
  background: "#f0f2f5",
  cursor: "pointer",
  "&:hover": {
    background: "#e9edef",
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: "#d9dae2",
  // borderBottom: '1px solid #f0f2f5',
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  borderRadius: "16px",
  height: "20px",
  fontSize: "0.75rem",
  marginLeft: "auto",
}));

const ContactsContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#d9dae2",
  overflow: "hidden",
  width: "100%",
}));

const ContactsList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: "#d9dae2",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#d9dae2",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.grey[400], 0.5),
    borderRadius: "3px",
    "&:hover": {
      background: alpha(theme.palette.grey[500], 0.7),
    },
  },
}));

const UserInfoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  color: "black",
  // borderBottom: '1px solid #f0f2f5',
  backgroundColor: "#d9dae2",
  // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
}));

const Contacts = ({ currentUser }) => {
  const [expandedPanels, setExpandedPanels] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { contacts, groups, setGroups, fetchUsersChat } =
    useContext(ChatContext);
  const [searchInput, setSearchInput] = useState("");
  const topContact = useRef();
  const dummy = useRef();

  const togglePanel = () => {
    setExpandedPanels(!expandedPanels);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  // Filter non-archived contacts
  const nonArchivedContacts = contacts.filter((contact) => !contact.isArchive);
  // Filter archived contacts
  const archivedContacts = contacts.filter((contact) => contact.isArchive);

  const archivedGroups = groups.filter((group) => group.isArchive);
  const nonArchivedGroups = groups.filter((group) => !group.isArchive);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const filteredContacts = nonArchivedContacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchInput.toLowerCase()),
  );

  const filteredGroups = nonArchivedGroups.filter((group) =>
    group.name.toLowerCase().includes(searchInput.toLowerCase()),
  );

  useEffect(() => {
    if (expandedPanels) {
      if (dummy?.current) dummy.current.scrollIntoView({ behavior: "smooth" });
    } else {
      if (topContact?.current)
        topContact.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [expandedPanels]);

  const totalArchived = archivedContacts.length + archivedGroups.length;
  const totalActive = filteredContacts.length + filteredGroups.length;

  return (
    <ContactsContainer
      className="contacts"
      sx={{
        height: "100%",
        minHeight: "89vh",
        maxHeight: "89vh",
      }}
    >
      <CreateGroup
        openDialog={openDialog}
        contacts={contacts}
        setGroups={setGroups}
        fetchUsersChat={fetchUsersChat}
        setOpenDialog={setOpenDialog}
      />

      {/* User Info Section */}
      <UserInfoSection>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
            >
              <StyledAvatar>
                {currentUser.username.charAt(0).toUpperCase()}
              </StyledAvatar>
            </StyledBadge>

            <Typography variant="h6" fontWeight={600}>
              {currentUser.username}
            </Typography>
          </Box>

          <LightTooltip title="New Chat" placement="left">
            <NewChatButton
              onClick={handleOpenDialog}
              size="small"
              sx={{
                backgroundColor: "#1976d2", // MUI primary blue
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#1565c0", // darker blue on hover
                },
                borderRadius: "8px",
                minWidth: "36px",
                height: "36px",
              }}
            >
              <EditIcon fontSize="small" />
            </NewChatButton>
          </LightTooltip>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mt: 2 }}>
          <SearchPaper elevation={0}>
            <SearchIcon sx={{ color: "text.primary", mr: 1 }} />
            {/* <InputBase
              sx={{ flex: 1, fontSize: '0.875rem'}}
              placeholder="Search contacts or groups..."
              value={searchInput}
              onChange={handleSearchInputChange}
            /> */}
            <InputBase
              sx={{
                flex: 1,
                fontSize: "0.875rem",
                color: "#5e5959",
                "& input::placeholder": {
                  opacity: 1,
                },
              }}
              placeholder="Search contacts or groups..."
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {searchInput && (
              <Fade in>
                <IconButton size="small" onClick={() => setSearchInput("")}>
                  <Typography
                    variant="caption"
                    color="primary"
                    fontWeight={500}
                  >
                    Clear
                  </Typography>
                </IconButton>
              </Fade>
            )}
          </SearchPaper>
        </Box>
      </UserInfoSection>

      {/* Contacts List */}
      <ContactsList className="contacts__list">
        <div ref={topContact} style={{ height: "1px" }}></div>

        {/* Contacts Section */}
        {filteredContacts.length > 0 && (
          <Box>
            <SectionHeader>
              <PersonIcon color="primary" fontSize="small" />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                CONTACTS
              </Typography>
              <StatsChip
                label={filteredContacts.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            </SectionHeader>

            {filteredContacts.map((contact, index) => (
              <Fade in timeout={300 + index * 50} key={contact._id}>
                <Box>
                  <ContactCard archive={""} contact={contact} index={index} />
                </Box>
              </Fade>
            ))}
          </Box>
        )}

        {/* Groups Section */}
        {filteredGroups.length > 0 && (
          <Box>
            <SectionHeader>
              <GroupIcon color="secondary" fontSize="small" />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                GROUPS
              </Typography>
              <StatsChip
                label={filteredGroups.length}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </SectionHeader>

            {filteredGroups.map((contact, index) => (
              <Fade in timeout={400 + index * 50} key={contact._id}>
                <Box>
                  <GroupCard contact={contact} index={index} />
                </Box>
              </Fade>
            ))}
          </Box>
        )}

        {/* No Results */}
        {searchInput &&
          filteredContacts.length === 0 &&
          filteredGroups.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
              }}
            >
              <SearchIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Try searching with different keywords
              </Typography>
            </Box>
          )}

        {/* Archive Section */}
        {totalArchived > 0 && (
          <Box sx={{ mt: 1 }}>
            <ArchiveSection onClick={togglePanel}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <ArchiveIcon color="action" fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Archived Chats
                  </Typography>
                  <Chip
                    label={totalArchived}
                    size="small"
                    sx={{ height: "20px", fontSize: "0.75rem" }}
                  />
                </Box>
                <IconButton
                  size="small"
                  sx={{
                    transform: expandedPanels
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>

              <Collapse in={expandedPanels} timeout={300}>
                <Divider sx={{ my: 1.5 }} />

                {archivedContacts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ ml: 1, mb: 1, display: "block" }}
                    >
                      ARCHIVED CONTACTS ({archivedContacts.length})
                    </Typography>
                    {archivedContacts.map((contact, index) => (
                      <ContactCard
                        archive={"1"}
                        key={contact._id}
                        contact={contact}
                        index={index}
                      />
                    ))}
                  </Box>
                )}

                {archivedGroups.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ ml: 1, mb: 1, display: "block" }}
                    >
                      ARCHIVED GROUPS ({archivedGroups.length})
                    </Typography>
                    {archivedGroups.map((contact, index) => (
                      <GroupCard
                        key={contact._id}
                        contact={contact}
                        index={index}
                      />
                    ))}
                  </Box>
                )}
              </Collapse>
            </ArchiveSection>

            <div ref={dummy} style={{ height: "1px" }}></div>
          </Box>
        )}

        {/* Empty State */}
        {!searchInput && totalActive === 0 && totalArchived === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#008069",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                opacity: 0.8,
              }}
            >
              <GroupIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              No Conversations Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start a new conversation by clicking the button above
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: "20px",
                backgroundColor: "#008069",
                color: "white",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#007561",
                },
              }}
              onClick={handleOpenDialog}
            >
              <EditIcon fontSize="small" />
              <Typography variant="body2" fontWeight={500}>
                Create New Chat
              </Typography>
            </Box>
          </Box>
        )}
      </ContactsList>
    </ContactsContainer>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.user.currentUser,
  features: state.features.features,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentUser: (user) => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Contacts);
